import React from "react";

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { InterfaceIcon } from "./InterfaceIcon";

import type { InterfaceDef } from "@/shared/types";

/** Each named field's value, read once, when the form is sent. */
const valuesOf = (form: HTMLFormElement): Record<string, string> =>
  Object.fromEntries(
    Array.from(form.elements).flatMap((el) => (el instanceof HTMLInputElement && el.name ? [[el.name, el.value]] : [])),
  );

/* The frame every interface dialog shares: the channel's icon and title on top,
   a body that scrolls, the buttons pinned below.

   With `onSubmit`, body and buttons are one form, so Enter in a field sends it,
   and the handler gets the named fields' values. That is how a token is read: at
   the moment it is sent, from the field, without ever being copied into React
   state. */
export function InterfaceDialog({ meta, title, description, footer, onSubmit, onClose, children }: {
  meta: InterfaceDef;
  title: React.ReactNode;
  description: React.ReactNode;
  footer?: React.ReactNode;
  onSubmit?: (values: Record<string, string>) => void;
  onClose: () => void;
  children?: React.ReactNode;
}) {
  const content = (
    <>
      <div className="min-w-0 flex-1 overflow-y-auto px-5 pb-5">
        <div className="space-y-3.5">{children}</div>
      </div>
      {footer && <div className="shrink-0 px-5 py-4 border-t hairline flex items-center justify-end gap-2">{footer}</div>}
    </>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="p-0 gap-0 w-[440px] max-w-[94vw] max-h-[86vh] flex flex-col rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
        <div className="shrink-0 px-5 pt-5 pb-4 flex items-start gap-3">
          <InterfaceIcon meta={meta} size={40} iconSize={20} rounded={11} />
          <div className="min-w-0">
            <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">{title}</DialogTitle>
            <DialogDescription className="text-[13px] leading-[1.5] text-[var(--text-tertiary)] mt-0.5">
              {description}
            </DialogDescription>
          </div>
        </div>
        {onSubmit ? (
          <form
            className="min-h-0 flex-1 flex flex-col"
            onSubmit={(e) => {
              e.preventDefault();
              onSubmit(valuesOf(e.currentTarget));
            }}
          >
            {content}
          </form>
        ) : (
          content
        )}
      </DialogContent>
    </Dialog>
  );
}
